import { CircleHelp } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import FreshpaintLogoLight from "@/components/FpLogoLight";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { Typography } from "@/design-system/components/Typography/Typography";
import PageWithTitle from "@/PageWithTitle";
import Risk from "@/trackers/Risk";

import { fetchPublicTrackerDetails, type PublicTrackerDetails } from "./api";

const loginPath = (trackerKey: string) => {
  const next = `/monitoring/trackingTools/${encodeURIComponent(trackerKey)}`;
  return `/login?${new URLSearchParams({ next }).toString()}`;
};

function DetailCard({
  title,
  children,
  className = "",
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`border-borderSubtle flex flex-col gap-2 rounded-md border bg-gray-50 p-4 ${className}`}
    >
      <Typography variant="heading-xs" as="h2">
        {title}
      </Typography>
      <div className="text-body-sm text-textMediumEmphasis leading-5">
        {children}
      </div>
    </section>
  );
}

function TrackerDetails({ details }: { details: PublicTrackerDetails }) {
  const whoCanFixIt = details.guidance.whoCanFixIt.trim();

  return (
    <div className="w-full max-w-[800px]">
      <article className="border-borderSubtle overflow-hidden rounded-xl border bg-white shadow-sm">
        <div
          className="flex flex-col items-start px-5 py-5 text-left"
          data-testid="tracker-summary"
        >
          <Risk
            riskLevel={details.riskLevel}
            description={details.description}
            infoMode="none"
          />
          <h1 className="text-heading-lg text-textDefault mt-2 tracking-tight">
            {details.trackerName}
          </h1>
        </div>

        <div
          className={
            whoCanFixIt
              ? "border-borderSubtle grid grid-cols-1 gap-3 border-t p-4 sm:p-6 lg:grid-cols-3"
              : "border-borderSubtle grid grid-cols-1 gap-3 border-t p-4 sm:p-6 lg:grid-cols-2"
          }
          data-testid="tracker-guidance-grid"
        >
          <DetailCard title="Why it's flagged">
            {details.guidance.whyFlagged}
          </DetailCard>
          <DetailCard title="Common fixes">
            {details.guidance.whatToDo}
          </DetailCard>
          {whoCanFixIt && (
            <DetailCard title="Who can fix it">{whoCanFixIt}</DetailCard>
          )}
          <DetailCard
            title="Where this typically loads"
            className={whoCanFixIt ? "lg:col-span-3" : "lg:col-span-2"}
          >
            <p>
              Usually added through a tag manager or directly in site code.
              Commonly appears as a script or image tag.
            </p>
          </DetailCard>
        </div>

        <section className="border-borderSubtle border-t bg-gray-50 px-5 py-5 sm:px-8">
          <p className="text-body-sm text-textSubtle">
            Sign in to see exactly where this was found on your site.
          </p>
        </section>
      </article>
    </div>
  );
}

function PublicTrackerDetailPage() {
  const { trackerKey = "" } = useParams<{ trackerKey: string }>();
  const [details, setDetails] = useState<PublicTrackerDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setIsError(false);

    fetchPublicTrackerDetails(trackerKey)
      .then((result) => {
        if (active) {
          setDetails(result);
        }
      })
      .catch(() => {
        if (active) {
          setIsError(true);
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [trackerKey]);

  const title = details?.trackerName ?? "Tracker details";
  const signInHref = loginPath(trackerKey);

  return (
    <PageWithTitle title={title}>
      <div className="min-h-screen bg-gray-50">
        <header className="bg-slate-900 px-12">
          <div className="mx-auto flex max-w-[800px] flex-col items-start gap-3 py-6">
            <FreshpaintLogoLight
              className="h-auto w-28"
              aria-label="Freshpaint"
            />
            <p className="text-body-sm leading-5 text-gray-300">
              A tracker was shared from Freshpaint Web Tracker Monitoring.
            </p>
          </div>
        </header>
        <main className="flex justify-center px-12 py-8 sm:py-12">
          {isLoading && (
            <div className="flex min-h-80 items-center justify-center">
              <LoadingSpinner />
            </div>
          )}
          {!isLoading && isError && (
            <section className="border-borderSubtle mx-auto flex max-w-xl flex-col items-center gap-3 rounded-lg border bg-white p-8 text-center shadow-sm">
              <CircleHelp
                className="text-iconMinimal h-8 w-8"
                strokeWidth={1.5}
                aria-hidden
              />
              <h1 className="text-textDefault text-xl font-semibold">
                Tracker details are unavailable
              </h1>
              <p className="text-textSubtle text-sm">
                Check the link and try again.
              </p>
            </section>
          )}
          {!isLoading && !isError && details && (
            <TrackerDetails details={details} />
          )}
        </main>
        {!isLoading && !isError && details && (
          <p className="text-body-sm text-textSubtle px-4 pb-10 text-center">
            Already have a Freshpaint account?{" "}
            <a
              href={signInHref}
              className="text-interactive-selected cursor-pointer font-medium hover:underline"
            >
              Sign in
            </a>
          </p>
        )}
      </div>
    </PageWithTitle>
  );
}

export default PublicTrackerDetailPage;
